import {useCallback, useState} from 'react';
import {appRouter} from '@netx/core-next/instance/router';
import {dispatcher} from '@netx/core-next/instance/dispatcher';
import {session} from '@netx/core-auth/collections/singleton/session';
import {getPublicProperty} from '@netx/core-app-next/utils';
import {useCustomForm, deriveCustomFormValues} from '@netx/core-custom-form';
import {USER_DEFAULTS} from '@netx/core-user/constants';
import {NewUserProfile} from '@netx/core-user/types';
import {useReactQueryUserSelfRegistration} from '../api';
import {AuthPref, POST_LOGIN_HASH_NAME} from '../constants';
import {AuthMessage} from '../locales';
import {localStore} from '@netx/core-next/instance/localStore';

/**
 * Hook for user (self) registration
 */
export const useSelfRegistration = () => {
	const customForm = useCustomForm({name: 'register'});

	const [awaitingApproval, setAwaitingApproval] = useState(false);

	const selfRegisterMutation = useReactQueryUserSelfRegistration({
		onError(err) {
			dispatcher.trigger('auth:user:register:error', err);
		},
		onSuccess(userProfile) {
			setAwaitingApproval(false);

			// TODO: this may not belong here... this is kind of some domain logic but...
			if (getPublicProperty(AuthPref.REGISTER_AUTO_LOGIN, true)) {
				// Just log them in
				// get hash user navigated to app with, set without triggering, clean up
				const postLoginRoute = localStore.get(POST_LOGIN_HASH_NAME);
				postLoginRoute
					? appRouter.navigate(postLoginRoute, {trigger: false})
					: appRouter.home();

				localStore.set(POST_LOGIN_HASH_NAME, '');

				session.set({
					loginMsg: AuthMessage.REGISTRATION_SUCCESS,
					loginMsgClass: 'has-success',
				});

				// Perform a hard refresh to begin a new session with the newly registered user
				window.location.reload(true);
				return;
			}

			// Registration request was successful
			// Must wait for administraive approval
			// Netx.router.navigate('register/approval-needed', {trigger: true});
			setAwaitingApproval(true);
			dispatcher.trigger('auth:user:register:success', userProfile, true);
		},
	});

	const selfRegisterUser = useCallback(
		async (params: Parameters<typeof selfRegisterMutation.mutateAsync>[0]) => {
			// TODO: we can use `zod` here to coerce and validate on the way out
			// not neccesarily here but somewhere before we hit the server
			const data = deriveCustomFormValues<NewUserProfile, typeof params>(
				params,
				Object.keys(USER_DEFAULTS) as (keyof NewUserProfile)[],
			);
			const newUserData = {
				...data.internalValues,
				propertyNames: Object.keys(data.customValues),
				propertyValues: Object.values(data.customValues),
			};
			// TODO: we should get some zod validation going and coerce the data
			return selfRegisterMutation.mutateAsync(newUserData as any);
		},
		[customForm.data],
	);

	return {
		customForm,
		awaitingApproval,
		error: selfRegisterMutation.error,
		registering: selfRegisterMutation.isLoading,
		selfRegisterUser,
	};
};

export default useSelfRegistration;
